import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon, TrashIcon, EyeIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { IImageGeneration, ImageGenerationStatus, IUserModel } from '@packages/global';
import StatusBadge from './StatusBadge';
import LazyImage from '../common/LazyImage';
import MediaPreviewModal from '../common/MediaPreviewModal';

interface ImageGenerationCardProps {
  item: IImageGeneration;
  models: IUserModel[];
  onEdit: (item: IImageGeneration) => void;
  onDelete: (id: string) => void;
  onUpdate: (updatedItem: IImageGeneration) => void;
}

export default function ImageGenerationCard({ item, models, onEdit, onDelete, onUpdate }: ImageGenerationCardProps) {
  const { t, i18n } = useTranslation(['generation', 'common']);
  const [showPreview, setShowPreview] = useState(false);
  const [showReferenceImage, setShowReferenceImage] = useState(false);

  const getModelName = (modelId: string) => {
    const model = models.find(m => m.id === modelId);
    return model ? model.title : modelId;
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleString(i18n.language);
  };

  const handleDownload = async () => {
    if (!item.resultImageUrl) return;
    try {
      const response = await fetch(item.resultImageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `image-${item.id}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleCardClick = () => {
    if (item.resultImageUrl) {
      setShowPreview(true);
    }
  };

  return (
    <div
      className={`relative w-60 h-72 overflow-hidden rounded-2xl bg-bg-surface border border-border-subtle hover:shadow-[0_0_20px_rgba(255,92,0,0.15)] hover:border-primary hover:-translate-y-1 transition-all duration-300 ${item.resultImageUrl ? 'cursor-pointer' : ''}`}
      onClick={handleCardClick}
    >
      {/* 缩略图区域 */}
      <div className="absolute inset-0">
        {item.resultImageUrl ? (
          <LazyImage
            src={item.resultImageUrl}
            alt={t('generation:imageCard.generatedImage')}
            className="w-full h-full object-cover"
            placeholderClassName="w-full h-full"
          />
        ) : (
          <div className="w-full h-full bg-bg-elevated flex items-center justify-center">
            {item.status === ImageGenerationStatus.PENDING || item.status === ImageGenerationStatus.PROCESSING ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-400 text-sm">
                  {item.status === ImageGenerationStatus.PENDING ? t('generation:imageCard.pendingGeneration') : t('generation:imageCard.processing')}
                </p>
              </div>
            ) : (
              <div className="text-center text-gray-400">
                <div className="w-16 h-16 bg-bg-form rounded-lg mx-auto mb-2 flex items-center justify-center">
                  <span className="text-2xl">🖼️</span>
                </div>
                <p className="text-sm">{t('generation:imageCard.noImage')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 状态指示器 */}
      <div className="absolute top-3 right-3">
        <StatusBadge status={item.status} />
      </div>

      {/* 参考图片指示器 */}
      {item.referenceImageUrl && (
        <div className="absolute top-3 left-3" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShowReferenceImage(true)}
            className="bg-black/50 text-white/90 px-2 py-1 rounded-md text-xs font-medium border border-white/20 hover:bg-black/70 transition-colors backdrop-blur-sm cursor-pointer"
          >
            {t('generation:imageCard.referenceImage')}
          </button>
        </div>
      )}

      {/* 半透明底部信息区 */}
      <div className="absolute inset-x-0 bottom-0 pt-16 pb-4 px-4">
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 30%, rgba(0,0,0,0.3) 70%, transparent 100%)'
          }}
        ></div>

        <div className="relative z-10">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-semibold text-white truncate flex-1 mr-2">
              {getModelName(item.modelId)}
            </h4>
          </div>

          <div className="text-sm text-white/80 mb-2 line-clamp-2">{item.prompt}</div>

          {item.referenceImageUrl && (
            <div className="text-xs text-primary mb-1">{t('generation:imageCard.referenceImage')}</div>
          )}

          <div className="text-xs text-white/60 mb-3">{formatDate(item.createdAt)}</div>

          {item.status === ImageGenerationStatus.FAILED && item.resultMessage && (
            <div className="text-xs text-red-400 mb-3 line-clamp-1" title={item.resultMessage}>{item.resultMessage}</div>
          )}

          <div className="flex justify-between items-center text-xs" onClick={(e) => e.stopPropagation()}>
            <span className="text-white/60">{item.resolution} | {item.aspectRatio}</span>
            <div className="flex gap-1">
              {item.resultImageUrl && (
                <>
                  <button
                    onClick={() => setShowPreview(true)}
                    className="p-1.5 rounded-lg bg-black/40 hover:bg-black/60 text-white/80 hover:text-white transition-all backdrop-blur-sm"
                    title={t('generation:imageCard.previewImage')}
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleDownload}
                    className="p-1.5 rounded-lg bg-black/40 hover:bg-black/60 text-white/80 hover:text-white transition-all backdrop-blur-sm"
                    title={t('generation:imageCard.downloadImage')}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                  </button>
                </>
              )}
              <button
                onClick={() => onEdit(item)}
                className="p-1.5 rounded-lg bg-black/40 hover:bg-black/60 text-white/80 hover:text-white transition-all backdrop-blur-sm"
                title={t('generation:imageCard.createFromThis')}
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => onDelete(item.id)}
                className="p-1.5 rounded-lg bg-black/40 hover:bg-red-500/80 text-white/80 hover:text-white transition-all backdrop-blur-sm"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 预览弹窗 */}
      <MediaPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        type="image"
        src={item.resultImageUrl || ''}
        alt={t('generation:imageCard.generatedImage')}
      />

      <MediaPreviewModal
        isOpen={showReferenceImage}
        onClose={() => setShowReferenceImage(false)}
        type="image"
        src={item.referenceImageUrl || ''}
        alt={t('generation:imageCard.referenceImage')}
      />
    </div>
  );
}
